import type { InstantWinWinnerEntries } from '@/types'

import { createLogger } from '../lib/log'

const { log } = createLogger('HostMessagingService')

/**
 *
 */
export default class HostMessagingService extends EventTarget {
  /**
   * @param origin The origin of the host application
   */
  constructor(private origin: string) {
    super()
    // Ensure we're running inside a host application
    if (window === window.parent) {
      throw new Error('Host application not found')
    }
  }

  /**
   * Requests a new game session from the host application. This assumes the
   * transaction will be made server-side, with the resulting session ID being
   * returned here by `postMessage` from the host.
   *
   * @param gameId The unique identifier for the game to purchase tickets for
   * @param quantity The number of entries/plays to purchase
   * @returns the ID of the created session
   */
  async requestCreateGameSession(
    gameId: string,
    quantity: number = 1
  ): Promise<string> {
    const response = await this.request<{ game_session_id: string }>(
      'game-session-request',
      {
        game_code: gameId,
        ticket_quantity: quantity,
      }
    )
    return response.game_session_id
  }

  /**
   * Notifies the host application of a win.
   *
   * @param value
   */
  notifyWin(win: InstantWinWinnerEntries) {
    this.send('win', { value: win.prize_value })
  }

  /**
   * Requests the customer access token from the host application.
   * @returns the customers access token
   */
  async requestCustomerToken() {
    return this.request<{ token: string }>('request-customer-token')
  }

  /**
   * Requests a customer refresh token from the host application.
   * @returns the customers freshet token
   */
  async requestCustomerTokenRefresh(token: string) {
    const response = await this.request<{ token: string }>(
      'request-customer-token-refresh',
      { token }
    )
    return response
  }

  /**
   * Requests content from the host application and waits for a response.
   *
   * @param type The message type to send
   * @param data The message payload
   */
  private request<T>(type: string, data?: any): Promise<T> {
    const targetOrigin = this.origin

    return new Promise((resolve, reject) => {
      let requestId = ''

      function onMessage(event: MessageEvent) {
        if (event.origin !== targetOrigin) {
          return
        }

        const msg = event.data

        if (msg.requestId === requestId) {
          // remove the listener
          window.removeEventListener('message', onMessage)

          if (msg.error) {
            log(`[${requestId}::ERR] ${type}`, msg.error.code)
            reject(new Error(msg.error.code))
            return
          }

          log(`[${requestId}::RES] ${type}`, msg.data)
          resolve(msg.data)
        }
      }

      window.addEventListener('message', onMessage)
      requestId = this.send(type, data)
    })
  }

  /**
   * Wraps a message in a standard envelope, assigns it an ID and dispatches it
   * to the host application.
   *
   * @param type The message type
   * @param data The message body
   * @returns the ID of the message
   */
  private send(type: string, data?: any): string {
    const requestId = crypto.randomUUID()
    log(`[${requestId}::REQ] ${type}`, data)
    window.parent.postMessage(
      {
        type,
        requestId,
        data,
      },
      this.origin
    )
    return requestId
  }

  private hasSession = false

  connect() {
    window.addEventListener('message', (event: MessageEvent) => {
      if (event.origin !== this.origin) {
        return
      }

      const { type, data } = event.data

      if (type === 'auth-state') {
        if (data.logged_in !== this.hasSession) {
          this.hasSession = data.logged_in
          this.dispatchEvent(
            new CustomEvent('authchange', { detail: data.logged_in })
          )
        }
      }
    })

    this.send('ready')
  }
}
